<template lang="html">
  <Transition name="fade" :duration="300" mode="out-in">
    <SignUpOnboardingStep
      v-if="onboardingStep"
      key="onboarding"
      @complete="handleOnboardingComplete"
    />

    <UForm
      v-else
      key="form"
      ref="$form"
      class="sign-up flex flex-col gap-4"
      :state="formState"
      :schema="rules"
      @submit="handleSubmit"
    >
      <div class="sign-up__welcome flex flex-col gap-3 text-left">
        <UIcon
          :name="brandIcons.logoLargeDark"
          class="h-9 w-auto max-w-48"
        />
        <div class="flex flex-col gap-1">
          <div class="text-lg font-semibold">
            {{ codeStep ? 'Введите код' : 'Создание аккаунта' }}
          </div>
          <div class="text-sm text-muted">
            {{ welcomeSubtitle }}
          </div>
        </div>
      </div>

      <AuthLoginTypeTabs
        v-if="showLoginTypeTabs && !codeStep"
        :model-value="props.type"
        :allowed-types="props.allowedLoginTypes"
        @update:model-value="emit('update:type', $event)"
      />

      <ApiErrorAlert v-if="serverError" :error="serverError" />

      <Transition name="fade" :duration="300" mode="out-in">
        <div v-if="!codeStep" class="sign-up__login">
          <UFormField name="login" required>
            <PhoneInput
              v-if="props.type === LoginInputType.phone"
              v-model="model.login"
              class="w-full"
            />
            <EmailInput
              v-else-if="props.type === LoginInputType.email"
              v-model="model.login"
              class="w-full"
            />
          </UFormField>
        </div>

        <CodeForm
          v-else-if="model.confirmation"
          :model="model"
          @complete="handleSubmit()"
          @resend="trySignUp()"
        />
      </Transition>

      <div class="sign-up__footer flex flex-col gap-3">
        <LegalConsentFields
          v-if="!codeStep"
          key="agree"
          v-model:consent-personal-data="consentPersonalData"
          v-model:accept-terms="acceptUserAgreement"
          mode="signup"
          class="sign-up__agree"
        />

        <div class="sign-up__actions">
          <UButton
            v-if="codeStep"
            key="submit"
            type="submit"
            label="Создать аккаунт"
            class="w-full justify-center"
            :loading="loading"
            :disabled="loading"
          />
          <UButton
            v-else
            key="next"
            label="Продолжить"
            type="submit"
            class="w-full justify-center"
            :loading="loading"
            :disabled="loading"
          />
        </div>

        <div
          v-if="!codeStep"
          key="links"
          class="sign-up__links flex flex-wrap gap-2 text-sm text-muted"
        >
          <span>Уже есть аккаунт?</span>
          <span
            class="cursor-pointer text-tertiary-500 hover:underline"
            @click="emit('goTo', AuthFormAction.signIn)"
          >Войти</span>
        </div>
      </div>
    </UForm>
  </Transition>
</template>

<script setup lang="ts">
import type { SignInCompletedResponse } from '@repo/api/web/models'
import * as yup from 'yup'

import AuthLoginTypeTabs from './AuthLoginTypeTabs.vue'
import CodeForm from './CodeForm.vue'
import EmailInput from './Input/EmailInput.vue'
import PhoneInput from './Input/PhoneInput.vue'
import SignUpOnboardingStep from './SignUpOnboardingStep.vue'
import { AuthFormAction, LoginInputType } from './type'

import { AuthModel } from '#Auth/models'
import ApiErrorAlert from '#Common/components/ApiErrorAlert.vue'
import LegalConsentFields from '#Info/components/LegalConsentFields.vue'
import { brandIcons } from '~/app/theme/brand'

const props = withDefaults(
  defineProps<{
    type: LoginInputType
    showLoginTypeTabs?: boolean
    allowedLoginTypes?: LoginInputType[]
  }>(),
  {
    showLoginTypeTabs: false,
    allowedLoginTypes: () => [LoginInputType.phone],
  },
)
const emit = defineEmits<{
  submit: []
  goTo: [action: AuthFormAction]
  'update:type': [type: LoginInputType]
}>()

const { service } = useAuth()
const model = ref(AuthModel.createLazy({ longSession: true }))
const codeStep = ref(false)
const onboardingStep = ref(false)
const loading = ref(false)
const { send, getError, clearError } = useRequest()
const toast = useToast()
const $form = useTemplateRef('$form')
const consentPersonalData = ref(false)
const acceptUserAgreement = ref(false)

const formState = computed(() => ({
  ...model.value,
  consentPersonalData: consentPersonalData.value,
  acceptUserAgreement: acceptUserAgreement.value,
}))

const serverError = computed(() => getError())
const welcomeSubtitle = computed(() => {
  if (!codeStep.value) {
    return 'Заказы, избранное и бонусы в одном месте'
  }

  return (
    model.value.confirmation?.description
    || `Код отправлен на ${model.value.login}`
  )
})
const rules = computed(() => {
  const base = {
    login:
      props.type === LoginInputType.email
        ? yup.string().email().required()
        : yup.string().required(),
    code: model.value.confirmation
      ? yup
          .string()
          .required()
          .min(model.value.confirmation.codeLength || 6)
      : yup.string(),
  }

  if (!codeStep.value) {
    return yup.object({
      ...base,
      consentPersonalData: yup
        .boolean()
        .oneOf([true], 'Необходимо согласие на обработку персональных данных')
        .required(),
      acceptUserAgreement: yup
        .boolean()
        .oneOf([true], 'Необходимо принять Пользовательское соглашение')
        .required(),
    })
  }

  return yup.object(base)
})

watchEffect(() => {
  if (serverError.value?.errors) {
    Object.entries(serverError.value.errors).forEach(([key, value]) => {
      $form.value?.setErrors([{ message: value[0]!, name: key }], key)
    })
  }
})

function resetLoginState() {
  model.value.login = ''
  model.value.code = undefined
  model.value.confirmation = undefined
  codeStep.value = false
  consentPersonalData.value = false
  acceptUserAgreement.value = false
  clearError()
}

watch(() => props.type, (type, prevType) => {
  if (prevType === undefined || type === prevType) {
    return
  }

  resetLoginState()
})

async function handleCompleated(completed: SignInCompletedResponse) {
  await service.signIn(completed.token!)
  onboardingStep.value = true
}

function handleOnboardingComplete() {
  emit('submit')
  toast.add({
    color: 'success',
    title: 'Аккаунт создан',
    description: 'Аккаунт успешно создан!',
  })
}
async function trySignUp() {
  try {
    loading.value = true
    const response = await send(() => service.signUp(model.value), {
      throwError: true,
      collectApiErrors: true,
    })

    model.value.confirmation = response
    codeStep.value = true
    clearError()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
async function handleCodeEnter() {
  try {
    loading.value = true

    const response = await send(
      () =>
        service.confirm({
          challengeId: model.value.confirmation!.challengeId!,
          code: model.value.code!,
        } as any),
      {
        throwError: true,
        collectApiErrors: true,
      },
    )

    await handleCompleated(response)

    clearError()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
function handleSubmit() {
  if (loading.value) return

  if (!codeStep.value) {
    return trySignUp()
  }

  return handleCodeEnter()
}
</script>
